import React, { useMemo, useState } from 'react';
import {
  Table, Tag, Space, Button, Card, Form, Input, Select, Typography,
  Drawer, DatePicker, InputNumber, message, Popconfirm, Tooltip, Empty
} from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { usePersistentState } from '../../../../utils/usePersistentState';
import { addPlatformLog, addEnterpriseLog, buildDetail, OP_TYPE } from '../../../../utils/auditLogStore';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

// ══════════════════════════════════════════════
//  常量
// ══════════════════════════════════════════════

// 当前登录企业（企业视角使用）
// TODO: 联调时替换为登录企业ID（getMyEntId()）
const CURRENT_ENT_ID = 'test-ent-001';
const CURRENT_ENT_NAME = 'test-深圳智创科技有限公司';

const STATUS_MAP = {
  planning: { label: '计划中', color: 'blue' },
  shipped: { label: '已出货', color: 'orange' },
  completed: { label: '已完成', color: 'green' },
  cancelled: { label: '已取消', color: 'default' },
};

// 操作日志字段标签
const LOG_LABELS = {
  targetMarket: '下游/目标市场',
  product: '产品',
  quantity: '数量',
  planDate: '计划出货时间',
  actualDate: '实际出货时间',
  status: '状态',
  logistics: '物流信息',
  remark: '备注',
};

// 转成操作日志展示字段（状态等转成文案）
const toLogView = (r) => ({
  targetMarket: r.targetMarket,
  product: r.productName,
  quantity: r.quantity,
  planDate: r.planDate,
  actualDate: r.actualDate,
  status: STATUS_MAP[r.status]?.label || r.status,
  logistics: r.logistics,
  remark: r.remark,
});

// 产品/设备选项（本企业产品）
// TODO: 联调时替换为接口数据
const PRODUCT_OPTIONS = [
  { value: 'test-prod-001', label: 'test-4G低功耗电池摄像机（test-prod-001）' },
  { value: 'test-prod-002', label: 'test-智能云台摄像机-高配版（test-prod-002）' },
  { value: 'test-prod-005', label: 'test-NVR网络录像机-标准版（test-prod-005）' },
];

// ══════════════════════════════════════════════
//  Mock 数据
//  TODO: 联调时替换为接口数据，按 enterprise_id 查询
// ══════════════════════════════════════════════

const MOCK_SHIPMENTS = [
  {
    key: 's1', enterpriseId: 'test-ent-001', targetMarket: '华东区经销商',
    productId: 'test-prod-001', productName: 'test-4G低功耗电池摄像机',
    quantity: 2000, planDate: '2026-08-15', actualDate: '2026-08-18', status: 'completed',
    logistics: '顺丰 SF1234567890', remark: '首批量产出货，已签收',
  },
  {
    key: 's2', enterpriseId: 'test-ent-001', targetMarket: '华南区渠道商',
    productId: 'test-prod-002', productName: 'test-智能云台摄像机-高配版',
    quantity: 1200, planDate: '2026-09-20', actualDate: '2026-09-22', status: 'shipped',
    logistics: '京东 JD9876543210', remark: '在途中',
  },
  {
    key: 's3', enterpriseId: 'test-ent-001', targetMarket: '海外-东南亚',
    productId: 'test-prod-005', productName: 'test-NVR网络录像机-标准版',
    quantity: 800, planDate: '2026-10-25', actualDate: '', status: 'planning',
    logistics: '', remark: '待客户确认海运仓位',
  },
  {
    key: 's4', enterpriseId: 'test-ent-001', targetMarket: '华北区经销商',
    productId: 'test-prod-001', productName: 'test-4G低功耗电池摄像机',
    quantity: 500, planDate: '2026-07-05', actualDate: '', status: 'cancelled',
    logistics: '', remark: '客户调整市场策略，本批次取消',
  },
  {
    key: 's5', enterpriseId: 'test-ent-001', targetMarket: '西南区渠道商',
    productId: 'test-prod-002', productName: 'test-智能云台摄像机-高配版',
    quantity: 1500, planDate: '2026-11-10', actualDate: '', status: 'planning',
    logistics: '', remark: '',
  },
  {
    key: 's6', enterpriseId: 'test-ent-002', targetMarket: '华中区经销商',
    productId: 'test-prod-001', productName: 'test-4G低功耗电池摄像机',
    quantity: 600, planDate: '2026-09-01', actualDate: '2026-09-03', status: 'shipped',
    logistics: '德邦 DB5566778899', remark: '铺货',
  },
];

// ══════════════════════════════════════════════
//  主组件：出货计划 Tab
//  mode: platform（平台侧）/ enterprise（企业侧）
// ══════════════════════════════════════════════

const ShipmentPlanTab = ({ mode = 'platform', enterpriseId, isArchived }) => {
  const isEnterprise = mode === 'enterprise';
  // 平台侧：归档客户只读；企业侧：本企业可增删改
  const editable = isEnterprise ? true : !isArchived;

  const [filterForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [filters, setFilters] = useState({});
  const [records, setRecords] = usePersistentState('mock_shipment_plan', MOCK_SHIPMENTS);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  // 数据范围
  const scopedRecords = useMemo(() => {
    if (isEnterprise) return records.filter((r) => r.enterpriseId === CURRENT_ENT_ID);
    if (enterpriseId) return records.filter((r) => r.enterpriseId === enterpriseId);
    return records;
  }, [records, enterpriseId, isEnterprise]);

  // 筛选
  const filteredRecords = useMemo(() => {
    let list = [...scopedRecords];
    const f = filters;
    if (f.status) list = list.filter((r) => r.status === f.status);
    if (f.market) {
      const kw = f.market.toLowerCase();
      list = list.filter((r) => r.targetMarket.toLowerCase().includes(kw));
    }
    if (f.planRange && f.planRange[0] && f.planRange[1]) {
      const s = f.planRange[0].format('YYYY-MM-DD');
      const e = f.planRange[1].format('YYYY-MM-DD');
      list = list.filter((r) => r.planDate >= s && r.planDate <= e);
    }
    return list;
  }, [scopedRecords, filters]);

  const handleSearch = () => setFilters(filterForm.getFieldsValue());
  const handleReset = () => {
    filterForm.resetFields();
    setFilters({});
  };

  const openAdd = () => {
    setEditRecord(null);
    editForm.resetFields();
    editForm.setFieldsValue({ status: 'planning', quantity: 1 });
    setDrawerVisible(true);
  };

  const openEdit = (record) => {
    setEditRecord(record);
    editForm.setFieldsValue({
      ...record,
      planDate: record.planDate ? dayjs(record.planDate) : null,
      actualDate: record.actualDate ? dayjs(record.actualDate) : null,
    });
    setDrawerVisible(true);
  };

  // 写入操作日志（平台客服 → 平台操作日志；企业 → 企业操作日志）
  const emitLog = (entry) => {
    if (isEnterprise) {
      addEnterpriseLog({
        ...entry,
        module: '客服工具 > 出货计划',
        enterpriseName: CURRENT_ENT_NAME,
        enterpriseId: CURRENT_ENT_ID,
      });
    } else {
      addPlatformLog({ ...entry, module: '客户跟踪 > 出货计划' });
    }
  };

  const handleSave = () => {
    editForm.validateFields().then((values) => {
      const product = PRODUCT_OPTIONS.find((p) => p.value === values.productId);
      const payload = {
        ...values,
        productName: product ? product.label.split('（')[0] : values.productId,
        planDate: values.planDate?.format('YYYY-MM-DD') || '',
        actualDate: values.actualDate?.format('YYYY-MM-DD') || '',
        enterpriseId: editRecord?.enterpriseId || (isEnterprise ? CURRENT_ENT_ID : enterpriseId),
      };
      if (editRecord) {
        setRecords((prev) => prev.map((r) => (r.key === editRecord.key ? { ...r, ...payload } : r)));
        const before = toLogView(editRecord);
        const after = toLogView(payload);
        const detail = buildDetail(before, after, LOG_LABELS);
        const changedKeys = detail ? Object.keys(detail.after) : [];
        const isStatusOnly = changedKeys.length === 1 && changedKeys[0] === 'status';
        emitLog({
          opType: isStatusOnly ? OP_TYPE.status : OP_TYPE.update,
          summary: isStatusOnly
            ? `状态变更：${before.status} → ${after.status}`
            : `编辑出货计划：产品"${payload.productName}"，目标市场"${payload.targetMarket}"`,
          detail,
        });
        message.success('出货计划已更新');
      } else {
        const newRec = { key: `s${Date.now()}`, ...payload };
        setRecords((prev) => [newRec, ...prev]);
        emitLog({
          opType: OP_TYPE.create,
          summary: `新增出货计划：产品"${payload.productName}"，目标市场"${payload.targetMarket}"，数量 ${payload.quantity}，计划出货时间 ${payload.planDate}`,
          detail: buildDetail(null, toLogView(newRec), LOG_LABELS),
        });
        message.success('出货计划已新增');
      }
      setDrawerVisible(false);
      setEditRecord(null);
    }).catch(() => {});
  };

  const handleDelete = (record) => {
    setRecords((prev) => prev.filter((r) => r.key !== record.key));
    emitLog({
      opType: OP_TYPE.delete,
      summary: `删除出货计划：产品"${record.productName}"，目标市场"${record.targetMarket}"`,
      detail: buildDetail(toLogView(record), null, LOG_LABELS),
    });
    message.success('已删除');
  };

  const columns = [
    { title: '下游/目标市场', dataIndex: 'targetMarket', key: 'targetMarket', width: 150, ellipsis: true },
    {
      title: '产品', key: 'product', width: 220,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.productName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.productId}</Text>
        </div>
      ),
    },
    { title: '数量', dataIndex: 'quantity', key: 'quantity', width: 90, align: 'right', render: (v) => v?.toLocaleString() },
    { title: '计划出货时间', dataIndex: 'planDate', key: 'planDate', width: 130, sorter: (a, b) => a.planDate.localeCompare(b.planDate) },
    { title: '实际出货时间', dataIndex: 'actualDate', key: 'actualDate', width: 130, render: (v) => v || '-' },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (s) => {
        const m = STATUS_MAP[s];
        return m ? <Tag color={m.color} style={{ border: 'none' }}>{m.label}</Tag> : s;
      },
    },
    { title: '物流信息', dataIndex: 'logistics', key: 'logistics', width: 170, ellipsis: true, render: (v) => v || '-' },
    {
      title: '备注', dataIndex: 'remark', key: 'remark', width: 180, ellipsis: true,
      render: (v) => (v ? <Tooltip title={v}>{v}</Tooltip> : '-'),
    },
    ...(editable ? [{
      title: '操作', key: 'action', fixed: 'right', width: 120,
      render: (_, record) => (
        <Space size={0} split={<span style={{ color: '#e8e8e8' }}>|</span>}>
          <Button type="link" size="small" onClick={() => openEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除该出货计划？" onConfirm={() => handleDelete(record)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div>
      {/* 查询区 */}
      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Form form={filterForm} layout="inline" style={{ flexWrap: 'wrap', gap: '8px 0' }}>
          <Form.Item name="status" style={{ marginRight: 8, marginBottom: 0 }}>
            <Select style={{ width: 120 }} allowClear placeholder="状态">
              {Object.entries(STATUS_MAP).map(([k, v]) => <Option key={k} value={k}>{v.label}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="market" style={{ marginRight: 8, marginBottom: 0 }}>
            <Input style={{ width: 180 }} placeholder="下游/目标市场，输入test可查询" allowClear />
          </Form.Item>
          <Form.Item name="planRange" style={{ marginRight: 8, marginBottom: 0 }}>
            <RangePicker placeholder={['计划出货起', '计划出货止']} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 列表区 */}
      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text strong>共 </Text>
            <Text style={{ fontSize: 18, fontWeight: 600, color: '#1677ff' }}>{filteredRecords.length}</Text>
            <Text strong> 条出货计划</Text>
          </div>
          {editable && <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>新增出货计划</Button>}
        </div>
        <Table
          columns={columns}
          dataSource={filteredRecords}
          rowKey="key"
          scroll={{ x: 1280 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            defaultPageSize: 10,
            pageSizeOptions: ['10', '20', '50'],
          }}
          locale={{ emptyText: <Empty description={isEnterprise ? '暂无出货计划' : '该企业暂无出货计划'} /> }}
        />
      </Card>

      {/* 新增/编辑抽屉 */}
      <Drawer
        title={editRecord ? '编辑出货计划' : '新增出货计划'}
        width={640}
        open={drawerVisible}
        onClose={() => { setDrawerVisible(false); setEditRecord(null); }}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setDrawerVisible(false); setEditRecord(null); }}>取消</Button>
              <Button type="primary" onClick={handleSave}>保存</Button>
            </Space>
          </div>
        }
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="targetMarket" label="下游/目标市场" rules={[{ required: true, message: '请输入下游/目标市场' }]}>
            <Input placeholder="如：华东区经销商" />
          </Form.Item>
          <Form.Item name="productId" label="产品" rules={[{ required: true, message: '请选择产品' }]}>
            <Select placeholder="请选择本企业产品" showSearch optionFilterProp="children">
              {PRODUCT_OPTIONS.map((p) => <Option key={p.value} value={p.value}>{p.label}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="quantity" label="数量" rules={[{ required: true, message: '请输入数量' }]}>
            <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="请输入数量" />
          </Form.Item>
          <Form.Item name="planDate" label="计划出货时间" rules={[{ required: true, message: '请选择计划出货时间' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="actualDate" label="实际出货时间">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
            <Select>
              {Object.entries(STATUS_MAP).map(([k, v]) => <Option key={k} value={k}>{v.label}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="logistics" label="物流信息">
            <Input placeholder="如：顺丰 SF1234567890" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <TextArea rows={3} placeholder="选填" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default ShipmentPlanTab;
